import { Component, OnInit, OnDestroy } from '@angular/core';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { FinancialReportService, FinancialReportResponse, DailyFinancialData, AggregatedFinancialData } from '../service/financial-report.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';

// Empty data structure for when API is not available
const emptyApiData: FinancialReportResponse = {};

@Component({
  selector: 'app-financial-dashboard',
  templateUrl: './financial-dashboard.component.html',
  styleUrls: ['./financial-dashboard.component.css']
})
export class FinancialDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  financialData: FinancialReportResponse = {};
  aggregatedData: AggregatedFinancialData | null = null;
  isLoading = false;
  error: string | null = null;
  hasApiError = false;
  dateRange = {
    startDate: '',
    endDate: ''
  };
  selectedPeriod = 'month';

  // Chart configurations
  public cashFlowChart: ChartConfiguration<'line'> = {
    type: 'line',
    data: {
      labels: [],
      datasets: [
        {
          label: 'Cash Total',
          data: [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Avoirs (Remboursements)',
          data: [],
          borderColor: 'rgb(249, 115, 22)', // Orange 500
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          tension: 0.4,
          fill: false,
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: (context: any) => {
              let label = `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`;
              
              // Find date using index
              const dateLabel = context.chart.data.labels?.[context.dataIndex] as string;
              if (dateLabel && this.financialData) {
                 // Try to find the matching original key
                 const originalDateKey = Object.keys(this.financialData).find(k => 
                    this.financialReportService.formatDate(k) === dateLabel
                 );
                 
                 if (originalDateKey && this.financialData[originalDateKey]) {
                    const dayData = this.financialData[originalDateKey];
                    const refunded = context.dataset.label === 'Avoirs (Remboursements)' ? dayData['refundedRevenue'] : 
                                    (context.dataset.label === 'Cash Total' ? dayData['refundedRevenue'] : 0);
                                    
                    if (refunded && refunded > 0) {
                        label += ` (Avoirs: -${refunded.toLocaleString()} MAD)`;
                    }
                 }
              }
              return label;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            },
            callback: function (value: any) {
              return value.toLocaleString() + ' MAD';
            }
          }
        },
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            }
          }
        }
      }
    }
  };

  public paidVsUnpaidBenefitChart: ChartConfiguration<'bar'> = {
    type: 'bar',
    data: {
      labels: [],
      datasets: [
        {
          label: 'Paid Benefit',
          data: [],
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
        },
        {
          label: 'Unpaid Benefit',
          data: [],
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            },
            callback: function (value: any) {
              return value.toLocaleString() + ' MAD';
            }
          }
        },
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            }
          }
        }
      }
    }
  };

  public paidVsUnpaidCapitalChart: ChartConfiguration<'bar'> = {
    type: 'bar',
    data: {
      labels: [],
      datasets: [
        {
          label: 'Capital Recouvré',
          data: [],
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
        },
        {
          label: 'Capital Engagé Non Recouvré',
          data: [],
          backgroundColor: 'rgba(251, 146, 60, 0.8)',
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            },
            callback: function (value: any) {
              return value.toLocaleString() + ' MAD';
            }
          }
        },
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            }
          }
        }
      }
    }
  };

  public benefitComparisonChart: ChartConfiguration<'line'> = {
    type: 'line',
    data: {
      labels: [],
      datasets: [
        {
          label: 'Paid Benefit',
          data: [],
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: 'rgb(34, 197, 94)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
        {
          label: 'Unpaid Benefit',
          data: [],
          borderColor: 'rgb(248, 113, 113)',
          backgroundColor: 'rgba(248, 113, 113, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: 'rgb(248, 113, 113)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            },
            callback: function (value: any) {
              return value.toLocaleString() + ' MAD';
            }
          }
        },
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11,
            }
          }
        }
      }
    }
  };

  public revenueDistributionChart: ChartConfiguration<'doughnut'> = {
    type: 'doughnut',
    data: {
      labels: ['Trésorerie Collectée', 'Prêts Impayés (Brut)', 'Créances Clients'],
      datasets: [{
        data: [],
        backgroundColor: [
          'rgba(34, 197, 94, 0.9)',
          'rgba(59, 130, 246, 0.9)',
          'rgba(248, 113, 113, 0.9)',
        ],
        borderWidth: 4,
        borderColor: '#ffffff',
        hoverBackgroundColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(248, 113, 113, 1)',
        ],
        hoverBorderWidth: 6,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: function (context: any) {
              const value = context.parsed;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${context.label}: ${value.toLocaleString()} MAD (${percentage}%)`;
            }
          }
        }
      }
    }
  };

  public benefitAnalysisChart: ChartConfiguration<'line'> = {
    type: 'line',
    data: {
      labels: [],
      datasets: [
        {
          label: 'Bénéfice Brut',
          data: [],
          borderColor: 'rgba(56, 189, 248, 0.7)', // Sky 400
          backgroundColor: 'rgba(56, 189, 248, 0.05)',
          borderWidth: 2,
          pointRadius: 4,
          pointBackgroundColor: 'rgb(56, 189, 248)',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Net Bénéfice',
          data: [],
          borderColor: 'rgb(16, 185, 129)', // Emerald 500
          backgroundColor: 'transparent',
          borderWidth: 4,
          pointRadius: 5,
          pointBackgroundColor: 'rgb(16, 185, 129)',
          pointBorderWidth: 2,
          pointBorderColor: '#ffffff',
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Impayés Brut',
          data: [],
          borderColor: 'rgb(239, 68, 68)', // Red 500
          backgroundColor: 'transparent',
          borderWidth: 3,
          pointRadius: 4,
          pointBackgroundColor: 'rgb(239, 68, 68)',
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Bénéfice Impayé',
          data: [],
          borderColor: 'rgb(245, 158, 11)', // Amber 500
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 3,
          pointBackgroundColor: 'rgb(245, 158, 11)',
          tension: 0.4,
          fill: true,
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          align: 'center',
          labels: {
            usePointStyle: true,
            pointStyleWidth: 10,
            padding: 16,
            boxWidth: 10,
            font: { size: 11, weight: 500 }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: (context: any) => {
              let label = `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`;
              // Add avoir annotation on days with refunds
              const dateLabel = context.chart.data.labels?.[context.dataIndex] as string;
              if (dateLabel && this.financialData) {
                const originalDateKey = Object.keys(this.financialData).find(k =>
                  this.financialReportService.formatDate(k) === dateLabel
                );
                if (originalDateKey && this.financialData[originalDateKey]) {
                  const dayData = this.financialData[originalDateKey];
                  const refRevenue = dayData['refundedRevenue'] || 0;
                  if (refRevenue > 0) {
                    if (context.dataset.label === 'Net Bénéfice') {
                      label += ` ⚠️ avoir déduit`;
                    }
                  }
                }
              }
              return label;
            }
          }
        }
      },
      scales: {
        y: { beginAtZero: true },
      }
    }
  };

  public capitalDistributionChart: ChartConfiguration<'pie'> = {
    type: 'pie',
    data: {
      labels: ['Paid Benefits', 'Unpaid Benefits', 'Paid Capital', 'Unpaid Capital'],
      datasets: [{
        data: [],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(251, 146, 60, 0.8)',
        ],
        borderWidth: 2,
        borderColor: '#ffffff',
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
              weight: 500,
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F9FAFB',
          bodyColor: '#F3F4F6',
          titleFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 14, weight: 'bold' },
          bodyFont: { family: "'Inter', 'Segoe UI', sans-serif", size: 13, weight: 500 },
          padding: 14,
          boxPadding: 8,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: function (context: any) {
              const value = context.parsed;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${context.label}: ${value.toLocaleString()} MAD (${percentage}%)`;
            }
          }
        }
      }
    }
  };

  // Table data
  paidTableData: any[] = [];
  unpaidTableData: any[] = [];
  totals: any = {
    totalCash: 0,
    totalPaidBenefit: 0,
    totalPaidCapital: 0,
    totalPaidRevenue: 0,
    totalUnpaidBenefit: 0,
    totalUnpaidCapital: 0,
    totalUnpaidRevenue: 0,
    totalPaidUnstockedCapital: 0,
    totalPaidStockedCapitalByBenefit: 0,
    totalUnpaidUnstockedCapital: 0,
    totalRealUnpaidLoans: 0,
    totalPaidLoan: 0,
    totalRawPaidBenefit: 0,
    totalUnpaidBrut: 0,
    totalRemainingUnpaid: 0,
    totalRawPaidRevenue: 0, // Added
    totalRefundedRevenue: 0 // Added
  };

  // Pagination
  currentPaidPage = 1;
  currentUnpaidPage = 1;
  itemsPerPage = 10;

  constructor(public financialReportService: FinancialReportService) { }

  ngOnInit(): void {
    // Scroll to top of the page when component initializes
    window.scrollTo(0, 0);

    // Initialize with loading state
    this.isLoading = true;

    this.setPeriod('month'); // Set default to current month
    this.loadFinancialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFinancialData(): void {
    this.isLoading = true;
    this.error = null;
    this.hasApiError = false;

    console.log('🔍 [DASHBOARD] Loading financial data for period:', this.dateRange);
    console.log('🔍 [DASHBOARD] Start date:', this.dateRange.startDate, 'End date:', this.dateRange.endDate);

    // Load aggregated data first (needed for totals)
    this.financialReportService.getAggregatedReports(this.dateRange.startDate, this.dateRange.endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (aggregatedData) => {
          console.log('✅ [DASHBOARD] Aggregated data loaded successfully:', aggregatedData);
          this.aggregatedData = aggregatedData;
          this.hasApiError = false;

          // Now load daily data for charts
          this.financialReportService.getDailyReports(this.dateRange.startDate, this.dateRange.endDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (dailyData) => {
                console.log('✅ [DASHBOARD] Daily data loaded successfully:', dailyData);
                this.financialData = dailyData;

                // Update all UI components
                this.updateCharts();
                this.updateTables();
                this.calculateTotals();
                this.isLoading = false;
              },
              error: (dailyError) => {
                console.warn('⚠️ [DASHBOARD] Daily data not available, using empty data for charts:', dailyError);
                this.financialData = emptyApiData;

                // Still update UI with aggregated data
                this.updateCharts();
                this.updateTables();
                this.calculateTotals();
                this.isLoading = false;
              }
            });
        },
        error: (aggregatedError) => {
          console.error('❌ [DASHBOARD] Error loading aggregated data:', aggregatedError);

          // Fallback to daily data only
          this.financialReportService.getDailyReports(this.dateRange.startDate, this.dateRange.endDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (dailyData) => {
                console.log('✅ [DASHBOARD] Daily data loaded successfully (fallback):', dailyData);
                this.financialData = dailyData;
                this.aggregatedData = null;
                this.hasApiError = false;

                this.updateCharts();
                this.updateTables();
                this.calculateTotals();
                this.isLoading = false;
              },
              error: (dailyError) => {
                console.error('❌ [DASHBOARD] Error loading both aggregated and daily data:', dailyError);
                // Set empty data when both APIs fail
                this.financialData = emptyApiData;
                this.aggregatedData = null;
                this.hasApiError = true;
                this.error = 'API non disponible - Aucune donnée à afficher';
                this.updateCharts();
                this.updateTables();
                this.calculateTotals();
                this.isLoading = false;
              }
            });
        }
      });
  }

  updateCharts(): void {
    const sortedDates = this.financialReportService.getSortedDates(this.financialData);
    const formattedDates = sortedDates.map(date => this.financialReportService.formatDate(date));

    // Update Cash Flow Chart
    this.cashFlowChart.data.labels = formattedDates;
    this.cashFlowChart.data.datasets[0].data = sortedDates.map(date => this.financialData[date].cashTotal);
    this.cashFlowChart.data.datasets[1].data = sortedDates.map(date => this.financialData[date].refundedRevenue || 0);

    // Update Paid vs Unpaid Benefit Chart
    this.paidVsUnpaidBenefitChart.data.labels = formattedDates;
    this.paidVsUnpaidBenefitChart.data.datasets[0].data = sortedDates.map(date => this.financialData[date].paidBenefit);
    this.paidVsUnpaidBenefitChart.data.datasets[1].data = sortedDates.map(date => this.financialData[date].unpaidBenefit);

    // Update Paid vs Unpaid Capital Chart
    // Capital Encaissé = capital réellement payé par le client (hors absorption bénéfice)
    //   = (paidCapital - paidStockedCapitalByBenefit) + (paidUnstockedCapital - paidUnstockedCapitalByBenefit)
    // Capital Impayé Net = remainingUnpaid (après absorption)
    this.paidVsUnpaidCapitalChart.data.labels = formattedDates;
    this.paidVsUnpaidCapitalChart.data.datasets[0].data = sortedDates.map(date => {
      const stockedPaid = (this.financialData[date].paidCapital || 0)
        - (this.financialData[date].paidStockedCapitalByBenefit || 0);
      const unstockedPaid = (this.financialData[date].paidUnstockedCapital || 0)
        - (this.financialData[date].paidUnstockedCapitalByBenefit || 0);
      return Math.max(0, stockedPaid + unstockedPaid);
    });
    this.paidVsUnpaidCapitalChart.data.datasets[1].data = sortedDates.map(date => this.financialData[date].remainingUnpaid || 0);

    // Update Benefit Comparison Chart
    this.benefitComparisonChart.data.labels = formattedDates;
    this.benefitComparisonChart.data.datasets[0].data = sortedDates.map(date => this.financialData[date].paidBenefit);
    this.benefitComparisonChart.data.datasets[1].data = sortedDates.map(date => this.financialData[date].unpaidBenefit);

    // Update Benefit Analysis Chart
    // Dataset[0]: Bénéfice Brut (rawPaidBenefit - before absorption)
    // Dataset[1]: Bénéfice Net (paidBenefit - after paying all debts)
    // Dataset[2]: Total Impayés Brut (all debts: stocked + unstocked + loans before absorption)
    this.benefitAnalysisChart.data.labels = formattedDates;
    this.benefitAnalysisChart.data.datasets[0].data = sortedDates.map(date => this.financialData[date].rawPaidBenefit || 0);
    this.benefitAnalysisChart.data.datasets[1].data = sortedDates.map(date => this.financialData[date].paidBenefit || 0);
    this.benefitAnalysisChart.data.datasets[2].data = sortedDates.map(date => this.financialData[date].totalUnpaidBrut || 0);
    this.benefitAnalysisChart.data.datasets[3].data = sortedDates.map(date => this.financialData[date].unpaidBenefit || 0);

    // Update Revenue Distribution Chart (use aggregated data if available)
    if (this.aggregatedData) {
      // Distribution des Revenus:
      //   1. Trésorerie Collectée  = totalCash
      //   2. Prêts Impayés (Brut)  = net loans + absorbed by benefit = gross total loans
      //   3. Créances Clients      = totalUnpaidRevenue - totalPaidLoanByBenefit
      //      ⚠ When benefit absorbs a LOAN, it artificially inflates unpaidBenefit (and thus unpaidRevenue).
      //        However, when benefit absorbs stocked/unstocked capital, it merely shifts the debt
      //        from capital to benefit, so the order debt total remains correct.
      //        Thus, we ONLY subtract the portion of benefit that absorbed loans.
      const grossLoans = (this.aggregatedData.totalRealUnpaidLoans || 0)
        + (this.aggregatedData.totalPaidLoan || 0);
      const unpaidOrders = Math.max(0,
        (this.aggregatedData.totalUnpaidRevenue || 0) - (this.aggregatedData.totalPaidLoanByBenefit || 0)
      );
      this.revenueDistributionChart.data.datasets[0].data = [
        this.aggregatedData.totalCash || 0,
        grossLoans,
        unpaidOrders
      ];

      // Update Capital Distribution Chart
      // ✅ Use gross (pre-absorption) values for meaningful pie slices:
      //   - totalRawPaidBenefit: benefit before it was used to absorb unpaid capital
      //   - totalUnpaidBenefit:  unpaid benefit from unpaid orders (not absorbed)
      //   - totalPaidCapital - totalPaidStockedCapitalByBenefit: capital actually paid by client
      //   - totalRemainingUnpaid: what's truly at risk after all absorptions
      const stockedPaid = (this.aggregatedData.totalPaidCapital || 0)
        - (this.aggregatedData.totalPaidStockedCapitalByBenefit || 0);
      const unstockedPaid = (this.aggregatedData.totalPaidUnstockedCapital || 0)
        - (this.aggregatedData.totalPaidUnstockedCapitalByBenefit || 0);
      const clientPaidCapital = Math.max(0, stockedPaid + unstockedPaid);

      this.capitalDistributionChart.data.datasets[0].data = [
        this.aggregatedData.totalPaidBenefit || 0,
        this.aggregatedData.totalUnpaidBenefit || 0,
        clientPaidCapital,
        this.aggregatedData.totalRemainingUnpaid || 0
      ];
      // Update labels to reflect the corrected semantics
      this.capitalDistributionChart.data.labels = [
        'Bénéfice Net Payé',
        'Bénéfice Impayé',
        'Capital Recouvré',
        'Capital à Risque'
      ];
    } else {
      // No aggregated data available - set charts to empty to avoid showing mock data
      this.revenueDistributionChart.data.datasets[0].data = [0, 0];
      this.capitalDistributionChart.data.datasets[0].data = [0, 0, 0, 0];
    }
  }

  updateTables(): void {
    const sortedDates = this.financialReportService.getSortedDates(this.financialData);

    this.paidTableData = sortedDates.map(date => ({
      date: this.financialReportService.formatDate(date),
      cashTotal: this.financialData[date].cashTotal,
      paidBenefit: this.financialData[date].paidBenefit,
      paidCapital: this.financialData[date].paidCapital,
      paidRevenue: this.financialData[date].paidRevenue,
      paidUnstockedCapital: this.financialData[date].paidUnstockedCapital,
      paidStockedCapitalByBenefit: this.financialData[date].paidStockedCapitalByBenefit,
      totalPaidLoan: this.financialData[date].totalPaidLoan || 0
    }));

    this.unpaidTableData = sortedDates.map(date => ({
      date: this.financialReportService.formatDate(date),
      // Raw benefit (before absorption)
      rawPaidBenefit: this.financialData[date].rawPaidBenefit || 0,
      // Net benefit (after absorption of all debts)
      paidBenefit: this.financialData[date].paidBenefit || 0,
      // Unpaid benefit = not collected from client + absorbed by debts
      unpaidBenefit: this.financialData[date].unpaidBenefit || 0,
      // Debts breakdown
      unpaidCapital: this.financialData[date].unpaidCapital || 0,
      unpaidUnstockedCapital: this.financialData[date].unpaidUnstockedCapital || 0,
      realUnpaidLoans: this.financialData[date].realUnpaidLoans || 0,
      // Total before absorption
      totalUnpaidBrut: this.financialData[date].totalUnpaidBrut || 0,
      // Capital at risk (after absorption)
      remainingUnpaid: this.financialData[date].remainingUnpaid || 0,
      unpaidRevenue: this.financialData[date].unpaidRevenue || 0
    }));
  }

  /**
   * ✅ [FIX] Extracts strictly the CASH-RECOVERED capital, excluding the portion
   * that was "recovered" only by absorbing profit (absorption rules).
   */
  getDirectPaidCapital(day: any): number {
    if (!day) return 0;
    const totalPaid = day.paidCapital || 0;
    const absorbed = (day.paidStockedCapitalByBenefit || 0) + (day.paidUnstockedCapitalByBenefit || 0);
    return Math.max(0, totalPaid - absorbed);
  }

  calculateTotals(): void {
    console.log('🧮 [DASHBOARD] Calculating totals. Aggregated data available:', !!this.aggregatedData);

    if (this.aggregatedData) {
      // Use aggregated data for totals (real API data with business logic)
      this.totals = {
        totalCash: this.aggregatedData.totalCash,
        totalPaidBenefit: this.aggregatedData.totalPaidBenefit,
        totalUnpaidBenefit: this.aggregatedData.totalUnpaidBenefit,
        totalUnpaidCapital: this.aggregatedData.totalUnpaidCapital,
        totalUnpaidRevenue: this.aggregatedData.totalUnpaidRevenue,
        totalPaidUnstockedCapital: this.aggregatedData.totalPaidUnstockedCapital,
        totalPaidStockedCapitalByBenefit: this.aggregatedData.totalPaidStockedCapitalByBenefit,
        totalUnpaidUnstockedCapital: this.aggregatedData.totalUnpaidUnstockedCapital,
        totalRealUnpaidLoans: this.aggregatedData.totalRealUnpaidLoans,
        totalPaidLoan: this.aggregatedData.totalPaidLoan,
        totalRawPaidBenefit: this.aggregatedData.totalRawPaidBenefit,
        totalUnpaidBrut: this.aggregatedData.totalUnpaidBrut,
        totalRemainingUnpaid: this.aggregatedData.totalRemainingUnpaid,
        // ✅ [FIX] Exclude absorbed capital from Recovered Capital (as per user definition)
        // Capital Recouvré = Capital Paid - (Capital absorbed by benefit)
        totalPaidCapital: this.aggregatedData.totalPaidCapital - 
                         (this.aggregatedData.totalPaidStockedCapitalByBenefit || 0) - 
                         (this.aggregatedData.totalPaidUnstockedCapitalByBenefit || 0),
        // ✅ [FIX] CA Brut = Total Cash before refunds. Use Cash metrics because they include unstocked items.
        totalRawPaidRevenue: this.aggregatedData.totalCash + (this.aggregatedData.totalRefundedRevenue || 0),
        // ✅ [FIX] CA Net = Actual Cash remaining
        totalPaidRevenue: this.aggregatedData.totalCash,
        totalRefundedRevenue: this.aggregatedData.totalRefundedRevenue || 0
      };
      console.log('✅ [DASHBOARD] Totals calculated from aggregated data:', this.totals);
    } else {
      // No aggregated data available - set all totals to 0 to avoid showing mock data
      this.totals = {
        totalCash: 0,
        totalPaidBenefit: 0,
        totalPaidCapital: 0,
        totalPaidRevenue: 0,
        totalUnpaidBenefit: 0,
        totalUnpaidCapital: 0,
        totalUnpaidRevenue: 0,
        totalPaidUnstockedCapital: 0,
        totalPaidStockedCapitalByBenefit: 0,
        totalUnpaidUnstockedCapital: 0,
        totalRealUnpaidLoans: 0,
        totalPaidLoan: 0,
        totalRawPaidBenefit: 0,
        totalUnpaidBrut: 0,
        totalRemainingUnpaid: 0,
        totalRawPaidRevenue: 0, // Added
        totalRefundedRevenue: 0 // Added
      };
      console.log('⚠️ [DASHBOARD] No aggregated data available, totals set to 0');
    }
  }

  onDateRangeChange(): void {
    this.loadFinancialData();
  }

  refreshData(): void {
    this.loadFinancialData();
  }

  retryApiConnection(): void {
    this.hasApiError = false;
    this.error = null;
    this.loadFinancialData();
  }

  formatCurrency(value: number | undefined): string {
    if (!value || value === 0) return '0 MAD';

    if (value >= 1000) {
      const kValue = (value / 1000).toFixed(1);
      return `${kValue}k MAD`;
    }

    return `${value.toLocaleString()} MAD`;
  }

  // Pagination methods
  get paginatedPaidData(): any[] {
    const startIndex = (this.currentPaidPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.paidTableData.slice(startIndex, endIndex);
  }

  get paginatedUnpaidData(): any[] {
    const startIndex = (this.currentUnpaidPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.unpaidTableData.slice(startIndex, endIndex);
  }

  get totalPaidPages(): number {
    return Math.ceil(this.paidTableData.length / this.itemsPerPage);
  }

  get totalUnpaidPages(): number {
    return Math.ceil(this.unpaidTableData.length / this.itemsPerPage);
  }

  onPaidPageChange(page: number): void {
    this.currentPaidPage = page;
  }

  onUnpaidPageChange(page: number): void {
    this.currentUnpaidPage = page;
  }

  setPeriod(period: string): void {
    this.selectedPeriod = period;
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    switch (period) {
      case 'today':
        const todayStr = today.toISOString().split('T')[0];
        this.dateRange = {
          startDate: todayStr,
          endDate: todayStr
        };
        break;
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        this.dateRange = {
          startDate: weekAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
        break;
      case 'month':
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
        this.dateRange = {
          startDate: firstDayOfMonth.toISOString().split('T')[0],
          endDate: lastDayOfMonth.toISOString().split('T')[0]
        };
        break;
      case 'lastMonth':
        const firstDayOfLastMonth = new Date(currentYear, currentMonth - 1, 1);
        const lastDayOfLastMonth = new Date(currentYear, currentMonth, 0);
        this.dateRange = {
          startDate: firstDayOfLastMonth.toISOString().split('T')[0],
          endDate: lastDayOfLastMonth.toISOString().split('T')[0]
        };
        break;
    }

    this.loadFinancialData();
  }

  onPeriodSelect(event: any): void {
    const period = event.target.value;
    this.setPeriod(period);
  }

  hasData(): boolean {
    return Object.keys(this.financialData).length > 0;
  }

  // Math property for template
  Math = Math;

  // Environment for template access
  environment = environment;
}
